/**
 * Registration page component - Simplified form
 *
 * Collects only basic info (email, password, name).
 * Tenant/organization selection happens in the Setup Wizard after email verification.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { Loader2, ChevronLeft, ExternalLink, Info } from 'lucide-react';

import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Separator } from '../../components/ui/separator';
import { PasswordInput } from '../../components/ui/password-input';
import { LanguageSelector } from '../../components/ui/language-selector';
import { useAuth, AuthApiError, authApi, adaptCrossDomainUrl, launchSSO } from '../../shared/auth';
import { SSOButtonList } from '../../shared/auth/SSOButtonList';
import { type SSOProviderId } from '../../shared/auth/ssoProviders';
import { supportsAppleNativeSignIn, triggerAppleNativeSignIn } from '../../shared/auth/appleNativeSignIn';
import { setSSOItem } from '../../shared/auth/ssoStorage';
import { validatePassword, getPasswordHint } from '../../shared/utils/validation';
import { getOAuthRedirectUri, getAppleOAuthRedirectUri, getNativePlatform } from '../../shared/utils';
import { ConsentCheckboxes } from '../../shared/components/auth/ConsentCheckboxes';
import { getGenericErrorMessage } from "@/shared/utils/errors";
import { setupWizardApi } from '../../shared/api/setupWizardApi';
import { getExplicitLanguageChoice, useGeoLanguageDefault } from '../../i18n/useGeoLanguageDefault';

type Step = 'email' | 'subdomain_choice' | 'details';

interface FormData {
  email: string;
  password: string;
  confirmPassword: string;
  first_name: string;
  last_name: string;
}

export function RegisterPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const invitationToken = searchParams.get('invitation');

  const { t } = useTranslation('auth');
  const { t: tCommon } = useTranslation('common');
  const { register } = useAuth();

  // Default the UI language from the visitor's country (e.g. Hebrew in Israel)
  // unless they've explicitly chosen one or their browser already gave a
  // supported non-English language.
  useGeoLanguageDefault();

  const [step, setStep] = useState<Step>('email');
  const [formData, setFormData] = useState<FormData>({
    email: '',
    password: '',
    confirmPassword: '',
    first_name: '',
    last_name: '',
  });
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [contentProcessingConsent, setContentProcessingConsent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [ssoLoadingProvider, setSSOLoadingProvider] = useState<string | null>(null);
  const isSSOLoading = ssoLoadingProvider !== null;
  const [subdomainInfo, setSubdomainInfo] = useState<{ subdomain: string; url: string } | null>(null);
  // When the sysadmin approval gate is on, new accounts are reviewed before
  // activation. Disclose that up front — but only when it's actually true, so
  // the copy disappears automatically once the gate is turned off.
  const [requiresApproval, setRequiresApproval] = useState(false);

  useEffect(() => {
    setupWizardApi
      .getPublicTenantApprovalRequired()
      .then((gate) => setRequiresApproval(gate.require_tenant_approval))
      .catch(() => setRequiresApproval(false));
  }, []);

  const handleSSO = async (provider: SSOProviderId, providerName: string) => {
    setError(null);
    setSSOLoadingProvider(provider);

    try {
      // Native iOS Sign in with Apple — uses the system sheet via Capacitor
      // plugin instead of the OAuth web redirect.
      if (provider === 'apple' && supportsAppleNativeSignIn()) {
        const bundleId = (import.meta.env.VITE_IOS_BUNDLE_ID as string | undefined) || 'ai.panluma.app';
        const result = await triggerAppleNativeSignIn(bundleId);
        const callbackResp = await authApi.ssoNativeCallback(
          'apple',
          result.identityToken,
          bundleId,
          result.user || null,
        );
        sessionStorage.setItem('__panluma_native_sso_response', JSON.stringify(callbackResp));
        navigate('/auth/sso/callback?native=1');
        return;
      }

      const redirectUri = provider === 'apple' ? getAppleOAuthRedirectUri() : getOAuthRedirectUri();
      const nativePlatform = getNativePlatform();
      const stateExtra = nativePlatform ? { platform: nativePlatform } : undefined;
      const { auth_url, state } = await authApi.ssoInit(provider, redirectUri, stateExtra);
      setSSOItem('sso_state', state);
      setSSOItem('sso_redirect_uri', redirectUri);
      setSSOItem('sso_provider', provider);
      const result = await launchSSO(auth_url);
      if (result.status === 'cancelled') {
        setSSOLoadingProvider(null);
        return;
      }
      if (result.status === 'completed') {
        navigate(result.inAppPath);
        return;
      }
    } catch (err) {
      if (err instanceof AuthApiError) {
        setError(t('sso.providerError', { provider: providerName }));
      } else {
        setError(t('sso.error'));
      }
      setSSOLoadingProvider(null);
    }
  };

  const handleChange = (field: keyof FormData) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));
    if (fieldErrors[field]) {
      setFieldErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setFieldErrors({});

    const email = formData.email.trim().toLowerCase();

    if (!email) {
      setFieldErrors({ email: t('validation.emailRequired') });
      return;
    }
    if (!email.includes('@') || !email.includes('.')) {
      setFieldErrors({ email: t('validation.invalidEmailFormat') });
      return;
    }

    setIsLoading(true);

    try {
      // Check if email is available (still needed to prevent duplicate registrations)
      const result = await authApi.checkRegistration({
        email,
        invitation_token: invitationToken || undefined,
      });

      if (!result.email_available) {
        // If the email exists on a subdomain tenant, show a choice
        if (result.existing_subdomain && result.existing_subdomain_url) {
          setSubdomainInfo({
            subdomain: result.existing_subdomain,
            url: result.existing_subdomain_url,
          });
          setStep('subdomain_choice');
          setIsLoading(false);
          return;
        }
        setFieldErrors({ email: t('register.emailAlreadyExists') });
        setIsLoading(false);
        return;
      }

      // Email is available - proceed to details step
      setStep('details');
    } catch (err) {
      if (err instanceof AuthApiError) {
        if (err.details && Object.keys(err.details).length > 0) {
          setFieldErrors(err.details);
        } else {
          setError(err.message);
        }
      } else {
        setError(getGenericErrorMessage());
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleDetailsSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setFieldErrors({});

    const errors: Record<string, string> = {};

    const passwordValidation = validatePassword(formData.password);
    if (!passwordValidation.isValid) {
      errors.password = passwordValidation.error!;
    }

    if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = t('resetPassword.passwordMismatch');
    }

    if (!formData.first_name.trim()) {
      errors.first_name = t('validation.firstNameRequired');
    }

    if (!formData.last_name.trim()) {
      errors.last_name = t('validation.lastNameRequired');
    }

    if (!termsAccepted) {
      errors.terms_accepted = t('consent.termsRequired');
    }
    if (!contentProcessingConsent) {
      errors.content_processing_consent = t('consent.contentProcessingRequired');
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    setIsLoading(true);

    try {
      const email = formData.email.trim().toLowerCase();

      // Simple registration - no tenant info needed
      // Invitation token is passed if present (will be stored for setup wizard)
      await register({
        email,
        password: formData.password,
        first_name: formData.first_name.trim(),
        last_name: formData.last_name.trim(),
        invitation_token: invitationToken || undefined,
        // Only a language the visitor EXPLICITLY chose (LanguageSelector) is
        // persisted as the account preference / used for the verification
        // email — the ambient UI language may be a GeoIP guess and must never
        // become durable. Absent a choice the backend defaults to English.
        preferred_language: getExplicitLanguageChoice(),
        terms_accepted: termsAccepted,
        content_processing_consent: contentProcessingConsent,
      });

      navigate(`/registration-pending?email=${encodeURIComponent(email)}`);
    } catch (err) {
      if (err instanceof AuthApiError) {
        if (err.details && Object.keys(err.details).length > 0) {
          setFieldErrors(err.details);
        } else {
          setError(err.message);
        }
      } else {
        setError(getGenericErrorMessage());
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    setStep('email');
  };

  const isAnyLoading = isLoading || isSSOLoading;

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-background p-4">
      <Card className="max-w-[450px] w-full">
        <CardContent className="p-8">
          <div className="flex justify-end mb-2">
            <LanguageSelector variant="labeled" />
          </div>
          <div className="flex justify-center mb-2">
            <img src="/panluma-logo.png" alt={tCommon('app.name')} className="h-24" />
          </div>
          <p className="text-sm text-muted-foreground text-center mb-6">{t('register.title')}</p>

          {error && (
            <Alert variant="destructive" className="mb-4">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Step 1: Email */}
          {step === 'email' && (
            <>
              <SSOButtonList
                loadingProvider={ssoLoadingProvider as SSOProviderId | null}
                isAnyLoading={isAnyLoading}
                onSelect={handleSSO}
                // eslint-disable-next-line i18next/no-literal-string -- i18n key reference passed to component, not display text
                labelKey="sso.signUpWith"
              />


              <div className="relative my-6">
                <Separator />
                <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-card px-2 text-sm text-muted-foreground">
                  {t('sso.or')}
                </span>
              </div>

              <form onSubmit={handleEmailSubmit} noValidate>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="email">{t('register.email')} <span className="text-destructive">*</span></Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange('email')}
                      disabled={isAnyLoading}
                      autoComplete="email"
                      autoFocus
                      className={fieldErrors.email ? 'border-destructive' : ''}
                      data-testid="email-input"
                    />
                    {fieldErrors.email && <p className="text-sm text-destructive">{fieldErrors.email}</p>}
                  </div>

                  <Button type="submit" className="w-full" size="lg" disabled={isAnyLoading} data-testid="continue-button">
                    {isLoading ? <Loader2 className="h-5 w-5 animate-spin" /> : t('register.continue')}
                  </Button>

                  {requiresApproval && (
                    <div className="flex items-start gap-2 rounded-md border border-warning/30 bg-warning/10 p-3 text-sm text-foreground">
                      <Info className="mt-0.5 h-4 w-4 shrink-0 text-warning" />
                      <span>{t('register.approvalNotice')}</span>
                    </div>
                  )}

                  <p className="text-sm text-center">
                    {t('register.hasAccount')}{' '}
                    <Link to="/login" className="text-primary hover:underline">{t('register.signIn')}</Link>
                  </p>
                </div>
              </form>
            </>
          )}

          {/* Step: Subdomain Choice */}
          {step === 'subdomain_choice' && subdomainInfo && (
            <div className="space-y-4">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={handleBack}
                className="mb-2 -ml-2"
              >
                <ChevronLeft className="h-4 w-4 me-1 rtl:rotate-180" />
                {tCommon('app.back')}
              </Button>

              <div className="text-center space-y-2 mb-2">
                <h3 className="font-medium">{t('register.subdomainChoiceTitle', { subdomain: subdomainInfo.subdomain })}</h3>
                <p className="text-sm text-muted-foreground">{t('register.subdomainChoiceDescription')}</p>
              </div>

              <Button
                className="w-full"
                size="lg"
                onClick={() => {
                  const email = formData.email.trim().toLowerCase();
                  const adapted = adaptCrossDomainUrl(subdomainInfo.url);
                  const url = new URL(adapted);
                  url.searchParams.set('email', email);
                  window.location.href = url.toString();
                }}
              >
                <ExternalLink className="mr-2 h-4 w-4" />
                {t('register.signInToSubdomain', { subdomain: subdomainInfo.subdomain })}
              </Button>

              <Button
                variant="outline"
                className="w-full"
                size="lg"
                onClick={() => {
                  // Allow registration to proceed despite existing subdomain account
                  setSubdomainInfo(null);
                  setStep('details');
                }}
              >
                {t('register.registerNewAccount')}
              </Button>
            </div>
          )}

          {/* Step 2: Details */}
          {step === 'details' && (
            <form onSubmit={handleDetailsSubmit} noValidate>
              {/* Hidden email field for password manager - associates email with the password */}
              <input
                type="email"
                value={formData.email}
                autoComplete="username"
                readOnly
                tabIndex={-1}
                aria-hidden="true"
                style={{ position: 'absolute', opacity: 0, pointerEvents: 'none', width: 0, height: 0 }}
              />
              <div className="space-y-4">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={handleBack}
                  className="mb-2 -ml-2"
                >
                  <ChevronLeft className="h-4 w-4 me-1 rtl:rotate-180" />
                  {tCommon('app.back')}
                </Button>

                <div className="flex gap-4">
                  <div className="flex-1 space-y-2">
                    <Label htmlFor="first_name">{t('register.firstName')} <span className="text-destructive">*</span></Label>
                    <Input
                      id="first_name"
                      value={formData.first_name}
                      onChange={handleChange('first_name')}
                      disabled={isAnyLoading}
                      autoFocus
                      className={fieldErrors.first_name ? 'border-destructive' : ''}
                      data-testid="first-name-input"
                    />
                    {fieldErrors.first_name && <p className="text-sm text-destructive">{fieldErrors.first_name}</p>}
                  </div>
                  <div className="flex-1 space-y-2">
                    <Label htmlFor="last_name">{t('register.lastName')} <span className="text-destructive">*</span></Label>
                    <Input
                      id="last_name"
                      value={formData.last_name}
                      onChange={handleChange('last_name')}
                      disabled={isAnyLoading}
                      className={fieldErrors.last_name ? 'border-destructive' : ''}
                      data-testid="last-name-input"
                    />
                    {fieldErrors.last_name && <p className="text-sm text-destructive">{fieldErrors.last_name}</p>}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password">{t('register.password')} <span className="text-destructive">*</span></Label>
                  <PasswordInput
                    id="password"
                    value={formData.password}
                    onChange={handleChange('password')}
                    disabled={isAnyLoading}
                    autoComplete="new-password"
                    className={fieldErrors.password ? 'border-destructive' : ''}
                    data-testid="password-input"
                  />
                  <p className="text-xs text-muted-foreground">{fieldErrors.password || getPasswordHint()}</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">{t('register.confirmPassword')} <span className="text-destructive">*</span></Label>
                  <PasswordInput
                    id="confirmPassword"
                    value={formData.confirmPassword}
                    // eslint-disable-next-line i18next/no-literal-string -- form field identifier, not display text
                    onChange={handleChange('confirmPassword')}
                    disabled={isAnyLoading}
                    autoComplete="new-password"
                    className={fieldErrors.confirmPassword ? 'border-destructive' : ''}
                    data-testid="confirm-password-input"
                  />
                  {fieldErrors.confirmPassword && <p className="text-sm text-destructive">{fieldErrors.confirmPassword}</p>}
                </div>

                <ConsentCheckboxes
                  termsAccepted={termsAccepted}
                  contentProcessingConsent={contentProcessingConsent}
                  onTermsChange={setTermsAccepted}
                  onContentProcessingChange={setContentProcessingConsent}
                  disabled={isAnyLoading}
                  termsError={fieldErrors.terms_accepted}
                  contentProcessingError={fieldErrors.content_processing_consent}
                />

                <Button
                  type="submit"
                  className="w-full mt-6"
                  size="lg"
                  disabled={isAnyLoading || !termsAccepted || !contentProcessingConsent}
                  data-testid="register-button"
                >
                  {isLoading ? <Loader2 className="h-5 w-5 animate-spin" /> : t('register.submit')}
                </Button>

                <p className="text-sm text-center">
                  {t('register.hasAccount')}{' '}
                  <Link to="/login" className="text-primary hover:underline">{t('register.signIn')}</Link>
                </p>
              </div>
            </form>
          )}
        </CardContent>
      </Card>
      <p className="mt-4 text-xs text-muted-foreground text-center max-w-[400px]">
        <Trans
          i18nKey="auth:legal.footer"
          components={{
            privacyLink: <a href="https://www.panluma.ai/privacy-policy" target="_blank" rel="noopener noreferrer" className="underline hover:text-foreground" />,
            termsLink: <a href="https://www.panluma.ai/terms-of-service" target="_blank" rel="noopener noreferrer" className="underline hover:text-foreground" />,
          }}
        />
      </p>
    </div>
  );
}
